/* eslint-disable security/detect-object-injection */

import './AddInspectionTemplate.style.less'
import HeaderComponent from '../../../../Components/HeaderComponent/HeaderComponent'
import {
  Form,
  Input,
  Select,
  Button,
  Collapse,
  Row,
  Col,
  Modal,
  message,
  Radio,
  Tag,
  Tooltip,
  Switch,
} from 'antd'
import { InfoCircleOutlined, LockFilled, PlusOutlined } from '@ant-design/icons'
import { camelCase, find, isEmpty, last, orderBy, sortBy } from 'lodash'
import { useEffect, useRef, useState, useLayoutEffect } from 'react'
import TemplateSection from '../../../components/TemplateSection/TemplateSection'
import CollapseHeader from '../../../components/CollapseHeader/CollapseHeader'
import { useMutation, useQuery } from 'react-query'
import {
  getSingleTemplate,
  saveTemplate,
  updateTemplate,
} from '../../../template.transform'
import {
  interpolate,
  isEmptyValue,
  JSONStringify,
  parseCommaSeparatedOptions,
  titleCase,
  toJson,
} from '@/utils/common'
import { useNavigate, useParams } from 'react-router-dom'
import { MSG_FIELD_REQUIRED } from '@/constant/common'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { removeIsNew, validateSections } from '../../../helper'
import { getInspectionType } from '@/screens/SystemConfigure/DataManagment/DataManagement.transform'
import {
  useGetPropertiesHook,
  useOverallResultSettingHook,
} from '@/screens/QSF/Inspection/Inspection.hook'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'
import {
  COMPANY_SETTING_SHOW_SECTION_GROUP,
  COMPANY_SETTING_DEFECT_STATUS,
} from '@/constant/user'
import { QSF_QUERY_KEY } from '@/constant/qsf'
import { FIELD_INPUT_TYPE } from '@/screens/Order/Pages/Order.constant'

const { Panel } = Collapse
const { error } = Modal

const DEFAULT_SECTION_GROUP_OPTION = {
  status: false,
  value: '',
}

const DEFAULT_HIDDEN_OPTION = false

const DEFAULT_VALUE_OPTION = {
  status: false,
  value: null,
}

function AddListInspectionTemplate() {
  const [form] = Form.useForm()
  const [sections, setsections] = useState([])
  const param = useParams()
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [overallResultMode, setOverallResultMode] = useState<
    'default' | 'custom' | undefined
  >()
  const [selectedResults, setSelectedResults] = useState<string[]>([])
  const [defectStatus, setDefectStatus] = useState(false)
  const [defectStatusInput, setDefectStatusInput] = useState('')
  const defectStatusHydratedRef = useRef(false)
  const [partialInspection, setPartialInspection] = useState(false)
  const partialInspectionHydratedRef = useRef(false)

  const navigate = useNavigate()

  useEffect(() => {
    partialInspectionHydratedRef.current = false
    defectStatusHydratedRef.current = false
  }, [param.key])

  const { data: properties } = useGetPropertiesHook(['inspectionType'])

  const { data: overallResultSettingData, isLoading: isLoadingOverallResult } =
    useOverallResultSettingHook()
  const overallResultSetting: any[] =
    (overallResultSettingData as any)?.options ?? []
  const showError = (duplicates: any) => {
    error({
      title: 'Fail to save templates',
      onOk() {
        console.log('OK')
      },
      content: (
        <>
          <div>The following contain duplicates</div>
          {!isEmpty(duplicates.duplicatesSections) && (
            <Row>
              Sections:&nbsp;
              {duplicates.duplicatesSections.map((item) => {
                return <strong key={item}>{item}&nbsp;</strong>
              })}
            </Row>
          )}
          {!isEmpty(duplicates.duplicateSubSections) && (
            <Row>
              Sub Sections:&nbsp;
              {duplicates.duplicateSubSections.map((item) => {
                return <strong key={item}>{item}&nbsp;</strong>
              })}
            </Row>
          )}
          {!isEmpty(duplicates.duplicateCheckPoints) && (
            <Row>
              Checkpoints:&nbsp;
              {duplicates.duplicateCheckPoints.map((item) => {
                return <strong key={item}>{item}&nbsp;</strong>
              })}
            </Row>
          )}
        </>
      ),
    })
  }

  const { data, isLoading } = useQuery(
    ['GET_TEMPLATE_DETAIL', param.key],
    async () => {
      return await getSingleTemplate(param.key)
    },
    {
      enabled: !!param?.key,
    }
  )

  useEffect(() => {
    const tmpSelectedProperty = properties?.find(
      (item) => item.name === 'inspectionType'
    )
    setSelectedProperty(tmpSelectedProperty)
  }, [properties])

  const { data: inspectionType } = useQuery(
    ['GET_INSPECTION_TYPE'],
    async () => {
      const tmp = {
        reference_id: selectedProperty.id,
        name: 'inspectionType',
        page_size: 1000,
        page_number: 1,
      }
      return (await getInspectionType(tmp))?.data?.map((item) => {
        return {
          ...item,
          value: toJson(item.value),
        }
      })
    },
    {
      enabled: !!selectedProperty,
    }
  )

  const { data: sectionGroupSetting } = useQuery(
    [QSF_QUERY_KEY.GET_COMPANY_SETTING_SECTION_GROUP],
    async () => {
      const result = await getCompanySetting(
        'inspection',
        COMPANY_SETTING_SHOW_SECTION_GROUP
      )
      return result
    }
  )

  const { data: defectStatusSettingData } = useQuery(
    ['GET_COMPANY_SETTING_DEFECT_STATUS'],
    async () => {
      return await getCompanySetting('defect', COMPANY_SETTING_DEFECT_STATUS)
    }
  )

  const isDefectStatusCompanySettingEnabled = Boolean(
    defectStatusSettingData?.value?.status
  )

  const companySettingOptions = defectStatusSettingData?.value?.options ?? [
    'Improved',
    'Open',
    'Closed',
  ]

  const existingTemplateOptions = data?.option?.defect_status?.options

  const defaultDefectOptionsStr =
    Array.isArray(existingTemplateOptions) && existingTemplateOptions.length > 0
      ? existingTemplateOptions.join(', ')
      : companySettingOptions.join(', ')

  useLayoutEffect(() => {
    if (data && !partialInspectionHydratedRef.current) {
      const savedPartialInspection = data?.option?.partial_inspection?.status
      setPartialInspection(!!savedPartialInspection)
      partialInspectionHydratedRef.current = true
    }
  }, [data])

  useLayoutEffect(() => {
    if (
      defectStatusHydratedRef.current ||
      defectStatusSettingData === undefined
    )
      return
    if (!data && param.key) return

    const templateDefectStatus = data?.option?.defect_status
    const existsInTemplate = templateDefectStatus != null

    const toStr = (opts) =>
      Array.isArray(opts) ? opts.join(', ') : String(opts || '')

    setDefectStatus(
      existsInTemplate ? Boolean(templateDefectStatus?.status) : false
    )

    const str = toStr(
      existsInTemplate ? existingTemplateOptions ?? [] : companySettingOptions
    )
    setDefectStatusInput(str)
    form.setFieldValue('defectStatusOptions', str)

    defectStatusHydratedRef.current = true
  }, [data, defectStatusSettingData, param.key])

  useEffect(() => {
    if (!overallResultSettingData) return
    if (param.key && !data) return

    const settings = overallResultSettingData as any
    const defaultType = settings.types?.find((t: any) => t.default)
    const defaultSelected =
      settings.options
        ?.filter((opt: any) => opt.selected)
        .map((opt: any) => opt.name) || []
    const savedOverallResult = data?.option?.overall_result

    if (!param.key || !savedOverallResult) {
      setOverallResultMode(defaultType?.value || 'default')
      setSelectedResults(defaultSelected)
      return
    }
    const databaseOptions = savedOverallResult.custom?.options
    const hasSavedOptions = Boolean(databaseOptions?.length)

    setOverallResultMode(savedOverallResult.type || 'default')

    setSelectedResults(
      hasSavedOptions
        ? databaseOptions.map((opt: any) => (opt.name || '').toLowerCase())
        : defaultSelected
    )
  }, [overallResultSettingData, data, param.key])

  useEffect(() => {
    if (data && inspectionType) {
      form.setFieldValue('name', data.name)
      form.setFieldValue(
        'inspectionType',
        JSONStringify(
          find(
            inspectionType,
            (item) => item.value.name == data.inspection_type_name
          )
        )
      )
      const hydratedSections =
        data.template_value?.sections?.map((section) => ({
          ...section,
          options: {
            ...section.options,
            group: section.options?.group || {
              ...DEFAULT_SECTION_GROUP_OPTION,
            },
          },
          sub_sections: (section.sub_sections ?? []).map((sub) => ({
            ...sub,
            checkpoints: (sub.checkpoints ?? []).map((cp) => ({
              ...cp,
              options: {
                ...cp.options,
                hidden: cp.options?.hidden ?? DEFAULT_HIDDEN_OPTION,
                default_value: cp.options?.default_value || {
                  ...DEFAULT_VALUE_OPTION,
                },
              },
            })),
          })),
        })) ?? []
      setsections(hydratedSections)
    }
  }, [data, inspectionType])

  const onSave = useMutation(async () => {
    await form.validateFields()

    const duplicates = validateSections(sections)
    if (duplicates.error == true) {
      showError(duplicates)
      return
    }

    let hasEmptyField = false
    sections.forEach((section) => {
      if (!section.title || String(section.title).trim() === '') {
        hasEmptyField = true
      }
      if (
        section.options?.group?.status &&
        isEmptyValue(section.options?.group?.value)
      ) {
        hasEmptyField = true
      }

      ;(section.sub_sections ?? []).forEach((sub) => {
        if (!sub.title || String(sub.title).trim() === '') {
          hasEmptyField = true
        }
        ;(sub.checkpoints ?? []).forEach((cp) => {
          if (!cp.title || String(cp.title).trim() === '') {
            hasEmptyField = true
          }

          if (!cp.control_type || String(cp.control_type).trim() === '') {
            hasEmptyField = true
          }

          if (
            cp.options?.default_value?.status &&
            isEmptyValue(cp.options?.default_value?.value)
          ) {
            hasEmptyField = true
          }
        })
      })
    })

    const parsedDefectOptions = parseCommaSeparatedOptions(defectStatusInput)

    if (
      isDefectStatusCompanySettingEnabled &&
      defectStatus &&
      parsedDefectOptions.length === 0
    ) {
      hasEmptyField = true
    }

    if (hasEmptyField) {
      message.error(MSG_FIELD_REQUIRED)
      return
    }

    const cleanedSections = sections.map((section) => ({
      ...section,
      sub_sections: (section.sub_sections ?? []).map((sub) => ({
        ...sub,
        checkpoints: (sub.checkpoints ?? []).map((cp) => {
          const isTextOrNumber =
            cp.control_type === FIELD_INPUT_TYPE.TEXT ||
            cp.control_type === FIELD_INPUT_TYPE.NUMBER
          if (!isTextOrNumber) {
            const { hidden, default_value, ...restOptions } = cp.options || {}
            return { ...cp, options: restOptions }
          }
          const defaultValue = cp.options?.default_value?.value
          let normalizedValue = defaultValue
          if (defaultValue === '' || defaultValue === null) {
            normalizedValue =
              cp.control_type === FIELD_INPUT_TYPE.NUMBER ? null : ''
          } else if (cp.control_type === FIELD_INPUT_TYPE.NUMBER) {
            const numericValue = Number(defaultValue)
            normalizedValue = Number.isFinite(numericValue)
              ? numericValue
              : null
          }

          return {
            ...cp,
            options: {
              ...cp.options,
              default_value: {
                ...cp.options?.default_value,
                value: normalizedValue,
              },
            },
          }
        }),
      })),
    }))

    const savedOverallResult = data?.option?.overall_result
    const overallResultPayload: any = {
      type: overallResultMode ?? 'default',
    }

    if (overallResultMode === 'custom' || savedOverallResult) {
      overallResultPayload.custom = {
        options: (overallResultSetting ?? [])
          .filter((item: any) => selectedResults.includes(item.name))
          .map((item: any) => ({
            name: item.name,
            value: item.value,
            color: item.color,
          })),
      }
    }

    let defectStatusPayload: any
    if (isDefectStatusCompanySettingEnabled) {
      if (defectStatus) {
        defectStatusPayload = {
          status: true,
          options: parsedDefectOptions,
        }
      } else {
        const hasExisting =
          Array.isArray(existingTemplateOptions) &&
          existingTemplateOptions.length > 0

        defectStatusPayload = {
          status: false,
          options: hasExisting
            ? existingTemplateOptions
            : companySettingOptions,
        }
      }
    } else {
      defectStatusPayload = data?.option?.defect_status
    }

    if (param.key) {
      const name = form.getFieldValue('name')
      const payload = {
        id: param.key,
        name: name,
        inspection_type_id: JSON.parse(form.getFieldValue('inspectionType')).id,
        template_value: { name: name, sections: removeIsNew(cleanedSections) },
        option: {
          overall_result: overallResultPayload,
          ...(defectStatusPayload !== undefined
            ? { defect_status: defectStatusPayload }
            : {}),
          partial_inspection: { status: partialInspection },
        },
      }

      await updateTemplate(payload)

      navigate(-1)
    } else {
      const name = form.getFieldValue('name')
      const payload = {
        name: name,
        inspection_type_id: JSON.parse(form.getFieldValue('inspectionType')).id,
        template_value: { name: name, sections: removeIsNew(cleanedSections) },
        option: {
          overall_result: overallResultPayload,
          ...(defectStatusPayload !== undefined
            ? { defect_status: defectStatusPayload }
            : {}),
          partial_inspection: { status: partialInspection },
        },
      }

      await saveTemplate(payload)
      navigate(-1)
    }
  })

  const handleSave = async () => {
    try {
      await onSave.mutateAsync()
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error(MSG_FIELD_REQUIRED)
      }
    }
  }

  const onSectionNameChange = (value, index) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    if (tmp[index].hasOwnProperty('is_new')) {
      tmp[index].name = camelCase(value)
    }
    tmp[index].title = String(value)
    setsections(tmp)
  }

  const onSubSectionNameChange = (sectionIndex, subSectionIndex, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    if (
      tmp[sectionIndex].sub_sections[subSectionIndex].hasOwnProperty('is_new')
    ) {
      tmp[sectionIndex].sub_sections[subSectionIndex].name = camelCase(value)
    }
    tmp[sectionIndex].sub_sections[subSectionIndex].title = String(value)
    setsections(tmp)
  }
  const onCheckpointNameChange = (
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    if (
      tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
        checkpointIndex
      ].hasOwnProperty('is_new')
    ) {
      tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
        checkpointIndex
      ].name = camelCase(value)
    }
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].title = value
    setsections(tmp)
  }

  const onDeleteSection = (index) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp.splice(index, 1)
    setsections(tmp)
  }

  const onDeleteSubSection = (sectionIndex, subSectionIndex) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections.splice(subSectionIndex, 1)
    setsections(tmp)
  }

  const onAddSubSection = (sectionIndex) => {
    const tmp: any = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections.push({
      title: '',
      name: '',
      is_new: true,
      options: {
        hasConclusion: 'NO',
        subSectionType: 'STANDARD',
      },
      index: last(tmp[sectionIndex].sub_sections)
        ? (last(tmp[sectionIndex].sub_sections) as any)?.index + 1
        : 0,
      checkpoints: [],
    })
    setsections(tmp)
  }

  const onAddSection = () => {
    setsections([
      ...sections,
      {
        title: '',
        name: '',
        is_new: true,
        options: {
          type: 'CUSTOM_SECTION',
          instruction: '',
          group: { ...DEFAULT_SECTION_GROUP_OPTION },
        },
        index: last(sections) ? last(sections).index + 1 : 0,
        sub_sections: [],
      },
    ])
  }

  const onAddCheckpoint = (sectionIndex, subSectionIndex) => {
    const tmp: any = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints.push({
      title: '',
      name: '',
      is_new: true,
      options: {
        hidden: DEFAULT_HIDDEN_OPTION,
        default_value: { ...DEFAULT_VALUE_OPTION },
      },
      control_type: '',
      index: last(tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints)
        ? (
            last(
              tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints
            ) as any
          )?.index + 1
        : 0,
      validations: {
        is_required: false,
      },
      data_source: [],
    })
    setsections(tmp)
  }

  const onDeleteCheckpoint = (
    sectionIndex,
    subSectionIndex,
    checkpointIndex
  ) => {
    const tmp: any = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints.splice(
      checkpointIndex,
      1
    )
    setsections(tmp)
  }

  const onChangeCheckpointControlType = (
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].control_type = value
    setsections(tmp)
  }

  const onCheckPointMandatory = (
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].validations.is_required = value
    setsections(tmp)
  }

  const onSelectOptionType = (index, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[index].options.type = value
    setsections(tmp)
  }

  const onChangeInstruction = (index, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[index].options.instruction = value
    setsections(tmp)
  }

  const onSubSectionHasConclusion = (sectionIndex, subSectionIndex, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    if (
      !tmp[sectionIndex].sub_sections[subSectionIndex].hasOwnProperty('options')
    ) {
      tmp[sectionIndex].sub_sections[subSectionIndex]['options'] = {}
    }
    if (
      !tmp[sectionIndex].sub_sections[subSectionIndex][
        'options'
      ].hasOwnProperty('hasConclusion')
    ) {
      tmp[sectionIndex].sub_sections[subSectionIndex]['options'][
        'hasConclusion'
      ] = {}
    }
    tmp[sectionIndex].sub_sections[subSectionIndex].options.hasConclusion =
      value
    setsections(tmp)
  }

  const onSubSectionType = (sectionIndex, subSectionIndex, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    if (
      !tmp[sectionIndex].sub_sections[subSectionIndex].hasOwnProperty('options')
    ) {
      tmp[sectionIndex].sub_sections[subSectionIndex]['options'] = {}
    }
    if (
      !tmp[sectionIndex].sub_sections[subSectionIndex][
        'options'
      ].hasOwnProperty('subSectionType')
    ) {
      tmp[sectionIndex].sub_sections[subSectionIndex]['options'][
        'subSectionType'
      ] = {}
    }
    tmp[sectionIndex].sub_sections[subSectionIndex].options.subSectionType =
      value
    setsections(tmp)
  }

  const onAdddatasource = (
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) => {
    if (isEmptyValue(value)) {
      message.warn('Value cannot be empty.', 1)
      return
    }
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].data_source.push({ label: value, value: value })
    setsections(tmp)
  }

  const onRemovedatasource = (
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    datasourceIndex
  ) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].data_source.splice(datasourceIndex, 1)
    setsections(tmp)
  }

  const onSubSectionInstructionChange = (
    sectionIndex,
    subSectionIndex,
    value
  ) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].options.instruction = value
    setsections(tmp)
  }

  const onGroupSectionChange = (index, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[index].options.group.status = value
    setsections(tmp)
  }

  const onGroupSectionNameChange = (index, value) => {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[index].options.group.value = value
    setsections(tmp)
  }

  function onCheckPointHidden(
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].options.hidden = value
    setsections(tmp)
  }

  function onCheckPointHasDefaultValue(
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) {
    const tmp = sortBy([...sections], 'index', 'asc')
    tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
      checkpointIndex
    ].options.default_value.status = value
    setsections(tmp)
  }

  function onCheckPointDefaultValueChange(
    sectionIndex,
    subSectionIndex,
    checkpointIndex,
    value
  ) {
    const tmp = sortBy([...sections], 'index', 'asc')
    const checkpoint =
      tmp[sectionIndex].sub_sections[subSectionIndex].checkpoints[
        checkpointIndex
      ]

    let newValue = value
    if (value === '' || value === null) {
      newValue = checkpoint.control_type === FIELD_INPUT_TYPE.NUMBER ? null : ''
    }

    checkpoint.options.default_value.value = newValue
    setsections(tmp)
  }

  const isSystemEmpty = (overallResultSettingData as any)?.isSystemEmpty
  const RESULT_CHIPS = overallResultSetting || []

  const customTypeData = (overallResultSettingData as any)?.types?.find(
    (t: any) => t.value === 'custom'
  )
  const MAX_OPTIONS = customTypeData?.max_options?.number || 4

  const toggleResult = (name: string) => {
    const lockedValues = RESULT_CHIPS.filter((c) => c.locked).map((c) => c.name)
    if (lockedValues.includes(name)) return

    if (selectedResults.includes(name)) {
      setSelectedResults(selectedResults.filter((r) => r !== name))
    } else {
      if (selectedResults.length >= MAX_OPTIONS) return
      setSelectedResults([...selectedResults, name])
    }
  }
  return (
    <>
      {onSave.isLoading && <LoadingComponent />}
      {isLoading && <LoadingComponent />}
      <HeaderComponent
        isBack
        title={`${param.key ? 'Edit' : 'New'} Inspection Template`}
      />
      <Form className="form-add-inspection-template" form={form}>
        <div className="input-row">
          <Form.Item
            labelCol={{ span: 5 }}
            className="input-full"
            name={'name'}
            label="Template Name"
            rules={[
              {
                required: true,
                whitespace: true,
                message: MSG_FIELD_REQUIRED,
              },
            ]}
          >
            <Input size="large" placeholder="Text" />
          </Form.Item>
        </div>
        <div className="input-row">
          <Form.Item
            label="Inspection Type"
            className="input-full"
            name={'inspectionType'}
            labelCol={{ span: 5 }}
            rules={[
              {
                required: true,
                whitespace: true,
                message: MSG_FIELD_REQUIRED,
              },
            ]}
          >
            <Select size="large" placeholder="Select">
              {inspectionType?.map((item: any, index: number) => {
                return (
                  <Select.Option key={index} value={JSONStringify(item)}>
                    {item?.value?.name}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>
        </div>
        {!isLoadingOverallResult && !isSystemEmpty && (
          <>
            <div className="input-row">
              <Form.Item
                label="Overall Result"
                className="input-full"
                name="overallResult"
                labelCol={{ span: 5 }}
                required
              >
                <div>
                  <Radio.Group
                    value={overallResultMode}
                    onChange={(e) => setOverallResultMode(e.target.value)}
                  >
                    <Radio value="default">Default</Radio>
                    <Radio value="custom">Custom</Radio>
                  </Radio.Group>
                </div>
              </Form.Item>
            </div>

            {overallResultMode === 'custom' && (
              <div className="input-row" style={{ marginTop: '-12px' }}>
                <Form.Item
                  className="input-full"
                  labelCol={{ span: 5 }}
                  wrapperCol={{ span: 19, offset: 5 }}
                  style={{ marginBottom: 24 }}
                >
                  <div className="overall-result-panel">
                    <div className="overall-result-counter">
                      <span className="overall-result-count-text">
                        {customTypeData?.selected_options_text
                          ? interpolate(customTypeData.selected_options_text, {
                              selected: selectedResults.length,
                              total: MAX_OPTIONS,
                            })
                          : `${selectedResults.length} / ${MAX_OPTIONS} results selected`}
                      </span>
                      {selectedResults.length >= MAX_OPTIONS && (
                        <span
                          className="overall-result-limit-msg"
                          style={{
                            color:
                              customTypeData?.max_options?.color || '#5B5B5B',
                          }}
                        >
                          &nbsp;
                          {customTypeData?.max_options?.text ||
                            '— Remove a result to add another'}
                        </span>
                      )}
                    </div>

                    <div className="overall-result-chips">
                      {RESULT_CHIPS.map((chip) => {
                        const isActive = selectedResults.includes(chip.name)
                        const isDisabled =
                          !isActive && selectedResults.length >= MAX_OPTIONS

                        return (
                          <Tag
                            key={chip.name}
                            className={`result-chip ${
                              isActive ? 'active' : 'inactive'
                            }${chip.locked ? ' locked' : ''}`}
                            role="button"
                            tabIndex={chip.locked || isDisabled ? -1 : 0}
                            aria-pressed={isActive}
                            aria-disabled={chip.locked || isDisabled}
                            style={{
                              borderColor: isActive
                                ? chip.color
                                : 'transparent',
                              color: isActive ? chip.color : '#848484',
                              cursor:
                                chip.locked || isDisabled
                                  ? 'not-allowed'
                                  : 'pointer',
                              opacity: isDisabled && !chip.locked ? 0.5 : 1,
                            }}
                            onClick={() =>
                              !chip.locked &&
                              !isDisabled &&
                              toggleResult(chip.name)
                            }
                            onKeyDown={(event) => {
                              if (
                                (event.key === 'Enter' || event.key === ' ') &&
                                !chip.locked &&
                                !isDisabled
                              ) {
                                event.preventDefault()
                                toggleResult(chip.name)
                              }
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              {['cio', 'na'].includes(chip.name.toLowerCase())
                                ? chip.value
                                : titleCase(chip.value)}
                              {chip.locked && (
                                <LockFilled
                                  style={{
                                    fontSize: 12,
                                    color: '#ADADAD',
                                  }}
                                />
                              )}
                              {chip.info && (
                                <Tooltip
                                  title={chip.info}
                                  overlayStyle={{ maxWidth: 'none' }}
                                >
                                  <InfoCircleOutlined
                                    style={{
                                      color: '#ADADAD',
                                      fontSize: 14,
                                      cursor: 'default',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </span>
                          </Tag>
                        )
                      })}
                    </div>
                  </div>
                </Form.Item>
              </div>
            )}
          </>
        )}

        {!isLoading && (
          <div className="input-row">
            <Form.Item
              label={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  Partial Inspection
                  <Tooltip
                    title="Enable to inspect a specific quantity instead of the full order."
                    overlayStyle={{ maxWidth: 'none' }}
                    overlayInnerStyle={{ fontSize: 14 }}
                  >
                    <InfoCircleOutlined
                      style={{ color: '#ADADAD', fontSize: 14 }}
                    />
                  </Tooltip>
                </span>
              }
              className="input-full"
              labelCol={{ span: 5 }}
              colon={false}
            >
              <Switch
                checked={partialInspection}
                onChange={(checked) => setPartialInspection(checked)}
              />
            </Form.Item>
          </div>
        )}
        {!isLoading && isDefectStatusCompanySettingEnabled && (
          <div className="input-row">
            <Form.Item
              label={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  Defect Status
                  <Tooltip
                    title="Enable defect status for inspection."
                    overlayStyle={{ maxWidth: 'none' }}
                    overlayInnerStyle={{ fontSize: 14 }}
                  >
                    <InfoCircleOutlined
                      style={{ color: '#ADADAD', fontSize: 14 }}
                    />
                  </Tooltip>
                </span>
              }
              className="input-full"
              labelCol={{ span: 5 }}
              colon={false}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Switch
                  checked={defectStatus}
                  onChange={(checked) => {
                    setDefectStatus(checked)
                    if (checked) {
                      const currentVal =
                        form.getFieldValue('defectStatusOptions') ??
                        defectStatusInput
                      if (!currentVal || currentVal.trim() === '') {
                        setDefectStatusInput(defaultDefectOptionsStr)
                        form.setFieldValue(
                          'defectStatusOptions',
                          defaultDefectOptionsStr
                        )
                        form.setFields([
                          { name: 'defectStatusOptions', errors: [] },
                        ])
                      }
                    } else {
                      form.setFields([
                        { name: 'defectStatusOptions', errors: [] },
                      ])
                    }
                  }}
                />
                {defectStatus && (
                  <Form.Item
                    name="defectStatusOptions"
                    style={{ marginBottom: 0, width: '50%' }}
                    rules={[
                      {
                        required: true,
                        message: MSG_FIELD_REQUIRED,
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Text"
                      onChange={(e) => setDefectStatusInput(e.target.value)}
                    />
                  </Form.Item>
                )}
              </div>
            </Form.Item>
          </div>
        )}

        <Collapse expandIconPosition="right">
          {orderBy(sections, ['index'], ['asc']).map((item: any, index) => {
            return (
              <Panel
                header={
                  <CollapseHeader
                    title="Section Name"
                    value={item}
                    onChangeName={(value) => onSectionNameChange(value, index)}
                    onDeleteSection={() => onDeleteSection(index)}
                    labelStyle={{ marginRight: 50 }}
                  />
                }
                key={index}
              >
                <TemplateSection
                  key={index}
                  item={item}
                  index={index}
                  onSubSectionInstructionChange={onSubSectionInstructionChange}
                  onAddSubSection={onAddSubSection}
                  onDeleteSubSection={onDeleteSubSection}
                  onSubSectionNameChange={onSubSectionNameChange}
                  onAddCheckpoint={onAddCheckpoint}
                  onDeleteCheckpoint={onDeleteCheckpoint}
                  onCheckpointNameChange={onCheckpointNameChange}
                  onSelectOptionType={onSelectOptionType}
                  onChangeInstruction={onChangeInstruction}
                  onSubSectionHasConclusion={onSubSectionHasConclusion}
                  onSubSectionType={onSubSectionType}
                  onChangeCheckpointControlType={onChangeCheckpointControlType}
                  onCheckPointMandatory={onCheckPointMandatory}
                  onAddDataSource={onAdddatasource}
                  onRemoveDatasource={onRemovedatasource}
                  onCheckPointHidden={onCheckPointHidden}
                  onCheckPointHasDefaultValue={onCheckPointHasDefaultValue}
                  onCheckPointDefaultValueChange={
                    onCheckPointDefaultValueChange
                  }
                  onGroupSectionChange={onGroupSectionChange}
                  onGroupSectionNameChange={onGroupSectionNameChange}
                  showSectionGroup={sectionGroupSetting?.value?.status}
                />
              </Panel>
            )
          })}
          <Button
            onClick={onAddSection}
            size="large"
            className="add-template-section-button"
          >
            <PlusOutlined className="plus-icon" />
            Add a Section
          </Button>
        </Collapse>
        <Row justify={'end'}>
          <Col>
            <Button
              className="button-add-template cancel-button"
              type="dashed"
              size="large"
              onClick={() => navigate(-1)}
              disabled={onSave.isLoading}
            >
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              onClick={handleSave}
              className="button-add-template"
              type="primary"
              size="large"
              disabled={onSave.isLoading}
            >
              Save
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  )
}

export default AddListInspectionTemplate
